import React, { useState, useEffect } from "react"
import { unlockStorageKey, getPinAttemptState, recordPinFailure, resetPinAttempts, getPinLength } from "../storage"

const NUMPAD = ["1","2","3","4","5","6","7","8","9","","0","⌫"]

const CSS = `
  .pin-root { scrollbar-width: none; }
  .pin-root::-webkit-scrollbar { display: none; }

  .pin-lock-icon { display: flex; }
  @media (max-height: 630px) { .pin-lock-icon { display: none !important; } }

  /* Fluid numpad sizing — scales with viewport height */
  .pin-numpad-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: clamp(7px, 1.5vh, 12px); }
  .pin-numpad-btn  { width: clamp(60px, 11vh, 82px); height: clamp(60px, 11vh, 82px); }
  .pin-numpad-fs   { font-size: clamp(20px, 4vh, 30px); }
  .pin-numpad-del  { font-size: clamp(17px, 3.2vh, 22px); }
  .pin-top         { padding-top: clamp(12px, 4vh, 32px); padding-bottom: clamp(6px, 1.5vh, 12px); }
  .pin-dots-wrap   { margin: clamp(14px, 2.8vh, 32px) 0 clamp(10px, 2.2vh, 28px); }
  .pin-err-wrap    { height: clamp(26px, 4vh, 34px); }

  @keyframes shake {
    0%,100%{ transform:translateX(0) }
    15%,45%,75%{ transform:translateX(-8px) }
    30%,60%,90%{ transform:translateX(8px) }
  }
  @keyframes lockPulse {
    0%{ transform:scale(1); box-shadow:0 12px 40px rgba(0,0,0,0.5), 0 0 0 0 rgba(106,179,243,0.3); }
    50%{ transform:scale(0.93); box-shadow:0 6px 20px rgba(0,0,0,0.4), 0 0 0 12px rgba(106,179,243,0); }
    100%{ transform:scale(1); box-shadow:0 12px 40px rgba(0,0,0,0.5), 0 0 0 0 rgba(106,179,243,0); }
  }
  @keyframes dotPop {
    0%{ transform:scale(0.6) }
    60%{ transform:scale(1.25) }
    100%{ transform:scale(1.1) }
  }
  .pin-key {
    transition: background 0.1s, transform 0.08s, box-shadow 0.1s !important;
    -webkit-tap-highlight-color: transparent;
    outline: none !important;
  }
  .pin-key:not(:disabled):active {
    transform: scale(0.88) !important;
    background: rgba(43,82,120,0.85) !important;
  }
`

function formatRemaining(ms) {
    const s = Math.max(0, Math.ceil(ms / 1000))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    return `${m}:${String(sec).padStart(2, "0")}`
}

function PinDots({ value, shake, length }) {
    // 6-digit mode gets slightly tighter spacing so the row doesn't overflow
    // narrow viewports.
    const gap = length === 6 ? 14 : 20
    return (
        <div className="pin-dots-wrap" style={{
            display: "flex", gap, justifyContent: "center",
            animation: shake ? "shake 0.5s" : "none",
        }}>
            {Array.from({ length }, (_, i) => {
                const filled = i < value.length
                return (
                    <div key={i} style={{
                        width: 16, height: 16, borderRadius: "50%",
                        background: filled ? "#6ab3f3" : "transparent",
                        border: `2px solid ${filled ? "#6ab3f3" : "rgba(106,179,243,0.25)"}`,
                        boxShadow: filled ? "0 0 14px rgba(106,179,243,0.7), 0 0 30px rgba(106,179,243,0.2)" : "none",
                        transform: filled ? "scale(1.1)" : "scale(1)",
                        animation: filled ? "dotPop 0.2s ease-out forwards" : "none",
                        transition: "border-color 0.2s, background 0.2s",
                    }} />
                )
            })}
        </div>
    )
}

export default function PinLock({ onUnlock }) {
    const [pin, setPin] = useState("")
    const [error, setError] = useState("")
    // Persisted across reloads — see storage.js cooldownMs ladder.
    const [attempts, setAttempts] = useState({ count: 0, lockedUntil: 0 })
    // PIN length is per-install (4 or 6) — read once from the stored blob.
    // Legacy installs without the field default to 4.
    const [pinLength, setPinLength] = useState(4)
    const [checking, setChecking] = useState(false)
    const [shake, setShake] = useState(false)
    const [lockAnim, setLockAnim] = useState(false)
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        getPinAttemptState().then(setAttempts).catch(() => {})
        getPinLength().then(setPinLength).catch(() => {})
    }, [])

    const locked = attempts.lockedUntil > now

    // Tick once a second while a cooldown is in effect so the countdown updates.
    useEffect(() => {
        if (!locked) return
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [locked])

    // Keep the error label in sync with the countdown / unlock transition.
    useEffect(() => {
        if (locked) {
            setError(`Locked — try again in ${formatRemaining(attempts.lockedUntil - now)}`)
        } else if (attempts.lockedUntil > 0 && attempts.lockedUntil <= now) {
            // Cooldown just expired — clear stale message, let user try again.
            setError("")
        }
    }, [now, locked, attempts.lockedUntil])

    async function handleDigit(d) {
        if (checking || locked) return
        if (d === "⌫") { setPin(p => p.slice(0, -1)); setError(""); return }
        if (!d) return
        const next = pin + d
        setPin(next)
        if (next.length < pinLength) return

        setChecking(true)
        const result = await unlockStorageKey(next)
        setChecking(false)

        if (result) {
            await resetPinAttempts()
            onUnlock(result)
            return
        }

        const newState = await recordPinFailure()
        setAttempts(newState)
        setNow(Date.now())
        setShake(true)
        setLockAnim(true)
        setTimeout(() => { setShake(false); setLockAnim(false) }, 600)
        setPin("")

        if (newState.lockedUntil > Date.now()) {
            setError(`Locked — try again in ${formatRemaining(newState.lockedUntil - Date.now())}`)
        } else {
            setError("Wrong PIN")
        }
    }

    return (
        <div className="pin-root" style={{
            minHeight: "100vh",
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "radial-gradient(ellipse 100% 70% at 50% 0%, #1c3550 0%, #17212b 55%)",
            boxSizing: "border-box",
            userSelect: "none",
            overflowY: "auto",
        }}>
            <style>{CSS}</style>

            {/* Top region: flex:1 centers content vertically when screen is tall enough.
                minHeight:fit-content prevents the region from collapsing below content height,
                so justifyContent:center never clips the icon at the top. */}
            <div className="pin-top" style={{
                flex: 1,
                minHeight: "fit-content",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                width: "100%", boxSizing: "border-box",
            }}>
                {/* Lock icon — hidden via CSS on viewports shorter than 630px */}
                <div className="pin-lock-icon" style={{
                    animation: lockAnim ? "lockPulse 0.55s ease" : "none",
                    width: 96, height: 96, borderRadius: 30,
                    background: "linear-gradient(150deg, #213d5a 0%, #152d47 100%)",
                    border: "1px solid rgba(106,179,243,0.2)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.07)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: 24,
                }}>
                    <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="#6ab3f3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2.5"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                </div>

                <div style={{ fontSize: 22, fontWeight: 700, color: "#e8f0f7", letterSpacing: "-0.5px" }}>
                    TrustGram
                </div>
                <div style={{ fontSize: 14, color: "#708499", marginTop: 6, letterSpacing: "0.1px" }}>
                    Enter your PIN to continue
                </div>

                <PinDots value={pin} shake={shake} length={pinLength} />

                <div className="pin-err-wrap" style={{ display: "flex", alignItems: "center" }}>
                    {error && (
                        <div style={{
                            fontSize: 12.5, color: "#ff9090",
                            background: "rgba(255,80,80,0.09)",
                            border: "1px solid rgba(255,80,80,0.2)",
                            borderRadius: 20, padding: "6px 16px",
                            letterSpacing: "0.1px",
                        }}>{error}</div>
                    )}
                </div>
            </div>

            {/* Numpad */}
            <div style={{
                paddingBottom: "max(28px, env(safe-area-inset-bottom, 28px))",
                paddingTop: 8, flexShrink: 0,
            }}>
                <div className="pin-numpad-grid">
                    {NUMPAD.map((d, i) => (
                        <button
                            key={i}
                            className={`pin-key pin-numpad-btn ${d === "⌫" ? "pin-numpad-del" : d ? "pin-numpad-fs" : ""}`}
                            onClick={() => handleDigit(d)}
                            disabled={!d || locked}
                            style={{
                                borderRadius: "50%",
                                fontWeight: d === "⌫" ? 400 : 300,
                                border: "none",
                                cursor: d ? "pointer" : "default",
                                background: d ? "rgba(31,43,56,0.95)" : "transparent",
                                color: d ? "#e8f0f7" : "transparent",
                                boxShadow: d ? "0 3px 10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
                                fontFamily: "inherit",
                                opacity: locked && d ? 0.4 : 1,
                            }}
                        >
                            {d}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}
